import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Text,
  useToast,
  Button,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  VStack,
  Flex,
  Badge,
} from '@chakra-ui/react';
import {
  getMyProfiles,
  updateMyProfile,
  createMyProfile,
  getNetworkTags,
  createNetworkTag,
  removeProfileImage,
} from '../../../services/skillProfileService';
import { useAuth } from '../../../contexts/AuthContext';
import ProfileHeaderSection from './ProfileHeaderSection';
import SocialSection from './SocialSection';
import TagsSection from './TagsSection';
import NetworkSectionCard from './NetworkSectionCard';
import NetworkProfilePreview from './NetworkProfilePreview';
import SocioSelector from './SocioSelector';
import ProfileVisibilityBanner from './ProfileVisibilityBanner';
import AccountPageShell from '../AccountPageShell';

const NETWORK_EMPTY_STATE_PROPS = {
  bg: '#36393f',
  borderRadius: '12px',
  borderWidth: '1px',
  borderColor: 'gray.600',
};

const normalizeTagIds = (arr) => [...(arr || [])].sort();
const arraysEqual = (a, b) => {
  const na = normalizeTagIds(a);
  const nb = normalizeTagIds(b);
  if (na.length !== nb.length) return false;
  return na.every((id, i) => id === nb[i]);
};

const isIdentityComplete = (draft) =>
  !!(draft?.fullName?.trim() && draft?.bio?.trim());

const isContactsComplete = (draft) =>
  !!(
    draft?.phone ||
    draft?.website ||
    draft?.instagram ||
    draft?.facebook ||
    draft?.tiktok ||
    draft?.twitch ||
    draft?.discord
  );

const isTagsComplete = (draft) => (draft?.tagIds || []).length > 0;

const NetworkProfiles = () => {
  const { userData } = useAuth();
  const soci = useMemo(() => userData?.tesseramenti || [], [userData]);

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSocioId, setSavingSocioId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedSocioId, setSelectedSocioId] = useState(null);
  const [draftBySocio, setDraftBySocio] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [newTagNames, setNewTagNames] = useState({
    profession: '',
    hobby: '',
    skill: '',
  });
  const toast = useToast();

  useEffect(() => {
    let isMounted = true;
    const fetchProfiles = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMyProfiles();
        if (!isMounted) return;
        setProfiles(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isMounted) return;
        setError('Errore nel caricamento dei tuoi profili Network.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchProfiles();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchTags = async () => {
      setTagsLoading(true);
      try {
        const tags = await getNetworkTags();
        if (!isMounted) return;
        setAllTags(Array.isArray(tags) ? tags : []);
      } catch (err) {
        if (!isMounted) return;
      } finally {
        if (isMounted) setTagsLoading(false);
      }
    };
    fetchTags();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedSocioId && soci.length > 0) {
      setSelectedSocioId(soci[0].documentId);
    }
  }, [soci, selectedSocioId]);

  useEffect(() => {
    if (!selectedSocioId) return;
    setDraftBySocio((prev) => {
      if (prev[selectedSocioId]?.documentId) return prev;
      const existing = profiles.find(
        (p) => p.socioDocumentId === selectedSocioId
      );
      const socio = soci.find((s) => s.documentId === selectedSocioId);
      const defaultFullName =
        `${socio?.nomeSocio || ''} ${socio?.cognomeSocio || ''}`.trim();
      const existingTagIds = existing?.tags?.map((t) => t.id) || [];
      return {
        ...prev,
        [selectedSocioId]: {
          documentId: existing?.documentId || null,
          isPublic: existing?.isPublic ?? false,
          bio: existing?.bio || '',
          fullName: existing?.fullName || defaultFullName || '',
          phone: existing?.phone || '',
          website: existing?.website || '',
          instagram: existing?.instagram || '',
          facebook: existing?.facebook || '',
          tiktok: existing?.tiktok || '',
          twitch: existing?.twitch || '',
          discord: existing?.discord || '',
          tagIds: existingTagIds,
        },
      };
    });
  }, [selectedSocioId, profiles, soci]);

  const currentDraft =
    (selectedSocioId && draftBySocio[selectedSocioId]) || null;
  const currentProfile = useMemo(
    () => profiles.find((p) => p.socioDocumentId === selectedSocioId) || null,
    [profiles, selectedSocioId]
  );

  const selectedSocio = useMemo(
    () => soci.find((s) => s.documentId === selectedSocioId),
    [soci, selectedSocioId]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!currentDraft || !selectedSocioId) return false;
    const saved = currentProfile;
    if (!saved && !currentDraft.documentId) {
      const hasAny =
        currentDraft.fullName ||
        currentDraft.bio ||
        currentDraft.tagIds?.length > 0 ||
        currentDraft.phone ||
        currentDraft.website ||
        currentDraft.instagram ||
        currentDraft.facebook ||
        currentDraft.tiktok ||
        currentDraft.twitch ||
        currentDraft.discord ||
        currentDraft.isPublic;
      return !!hasAny;
    }
    const savedTagIds = saved?.tags?.map((t) => t.id) || [];
    return (
      (saved?.fullName ?? '') !== (currentDraft.fullName ?? '') ||
      (saved?.isPublic ?? false) !== (currentDraft.isPublic ?? false) ||
      (saved?.bio ?? '') !== (currentDraft.bio ?? '') ||
      (saved?.phone ?? '') !== (currentDraft.phone ?? '') ||
      (saved?.website ?? '') !== (currentDraft.website ?? '') ||
      (saved?.instagram ?? '') !== (currentDraft.instagram ?? '') ||
      (saved?.facebook ?? '') !== (currentDraft.facebook ?? '') ||
      (saved?.tiktok ?? '') !== (currentDraft.tiktok ?? '') ||
      (saved?.twitch ?? '') !== (currentDraft.twitch ?? '') ||
      (saved?.discord ?? '') !== (currentDraft.discord ?? '') ||
      !arraysEqual(savedTagIds, currentDraft.tagIds || [])
    );
  }, [currentDraft, currentProfile, selectedSocioId]);

  const handleDraftChange = (field, value) => {
    if (!selectedSocioId) return;
    setDraftBySocio((prev) => ({
      ...prev,
      [selectedSocioId]: { ...(prev[selectedSocioId] || {}), [field]: value },
    }));
  };

  const toggleTagSelection = (tagId) => {
    if (!selectedSocioId) return;
    setDraftBySocio((prev) => {
      const current = prev[selectedSocioId] || {};
      const currentIds = current.tagIds || [];
      const nextIds = currentIds.includes(tagId)
        ? currentIds.filter((id) => id !== tagId)
        : [...currentIds, tagId];
      return { ...prev, [selectedSocioId]: { ...current, tagIds: nextIds } };
    });
  };

  const handleNewTagNameChange = (category, value) => {
    setNewTagNames((prev) => ({ ...prev, [category]: value }));
  };

  const handleAddTag = async (category) => {
    const name = newTagNames[category]?.trim();
    if (!name) return;
    try {
      const existing = allTags.find(
        (t) =>
          t.category === category && t.name.toLowerCase() === name.toLowerCase()
      );
      let tagToUse = existing;
      if (!tagToUse) {
        const created = await createNetworkTag({ name, category });
        if (created) {
          setAllTags((prev) => [...prev, created]);
          tagToUse = created;
        }
      }
      if (tagToUse) {
        toggleTagSelection(tagToUse.id);
        setNewTagNames((prev) => ({ ...prev, [category]: '' }));
      }
    } catch (err) {
      toast({
        title: 'Errore nella creazione del tag.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSave = async () => {
    if (!selectedSocioId || !currentDraft) return null;
    setSavingSocioId(selectedSocioId);
    try {
      const payload = {
        fullName: currentDraft.fullName,
        isPublic: currentDraft.isPublic,
        bio: currentDraft.bio,
        phone: currentDraft.phone,
        website: currentDraft.website,
        instagram: currentDraft.instagram,
        facebook: currentDraft.facebook,
        tiktok: currentDraft.tiktok,
        twitch: currentDraft.twitch,
        discord: currentDraft.discord,
        tagIds: currentDraft.tagIds || [],
      };
      const updated = currentDraft.documentId
        ? await updateMyProfile(currentDraft.documentId, payload)
        : await createMyProfile(selectedSocioId, payload);
      setProfiles((prev) => [
        ...prev.filter((p) => p.documentId !== updated.documentId),
        updated,
      ]);
      setDraftBySocio((prev) => ({
        ...prev,
        [selectedSocioId]: {
          ...prev[selectedSocioId],
          documentId: updated.documentId,
        },
      }));
      toast({
        title: currentDraft.isPublic
          ? 'Profilo salvato e pubblicato.'
          : 'Profilo salvato.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      return updated;
    } catch (err) {
      toast({
        title: 'Errore nel salvataggio del profilo.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return null;
    } finally {
      setSavingSocioId(null);
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedSocioId) return;
    setUploadingImage(true);
    try {
      let documentId = currentDraft?.documentId;
      if (!documentId) {
        const updated = await handleSave();
        documentId = updated?.documentId;
        if (!documentId)
          throw new Error(
            "Impossibile creare il profilo prima di caricare l'immagine."
          );
      }
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(
        `/api/account/skill-profiles/${encodeURIComponent(documentId)}/profile-image`,
        { method: 'POST', credentials: 'include', body: formData }
      );
      if (!response.ok) throw new Error(`Upload fallito: ${response.status}`);
      const data = await getMyProfiles();
      setProfiles(Array.isArray(data) ? data : []);
      toast({
        title: 'Immagine profilo aggiornata.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Errore nel caricamento dell'immagine.",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingImage(false);
      const inputEl = event.target;
      if (inputEl?.type === 'file') inputEl.value = '';
    }
  };

  const handleRemoveImage = async () => {
    if (!selectedSocioId || !currentDraft?.documentId) return;
    setUploadingImage(true);
    try {
      await removeProfileImage(currentDraft.documentId);
      const data = await getMyProfiles();
      setProfiles(Array.isArray(data) ? data : []);
      toast({
        title: 'Immagine profilo rimossa.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Errore nella rimozione dell'immagine.",
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const saveButton = (
    <Button
      colorScheme="yellow"
      color="black"
      onClick={handleSave}
      isDisabled={!hasUnsavedChanges}
      isLoading={savingSocioId === selectedSocioId || uploadingImage}
      loadingText={uploadingImage ? 'Caricamento immagine…' : 'Salvataggio…'}
      size={{ base: 'md', md: 'md' }}
      width={{ base: 'full', md: 'auto' }}
      flexShrink={0}
    >
      Salva modifiche
    </Button>
  );

  const networkStepBadges = (
    <Flex gap={2} flexWrap="wrap" mb={6}>
      <Badge colorScheme="gray" variant="outline" fontSize="xs">
        1 Identità
      </Badge>
      <Badge colorScheme="gray" variant="outline" fontSize="xs">
        2 Contatti
      </Badge>
      <Badge colorScheme="gray" variant="outline" fontSize="xs">
        3 Tag
      </Badge>
    </Flex>
  );

  const networkShellProps = {
    title: 'Il tuo profilo Network',
    titleId: 'network-title',
    subtitle:
      "Compila in 3 passaggi: identità, contatti e tag. L'anteprima si aggiorna mentre scrivi.",
  };

  const wrapNetworkPage = (content, shellPb) => (
    <AccountPageShell {...networkShellProps} pb={shellPb}>
      <Box color="white">
        {networkStepBadges}
        {content}
      </Box>
    </AccountPageShell>
  );

  if (loading) {
    return wrapNetworkPage(
      <Box {...NETWORK_EMPTY_STATE_PROPS} p={{ base: 4, md: 6 }}>
        <Skeleton height="120px" mb={4} borderRadius="lg" />
        <Skeleton height="40px" mb={4} />
        <VStack align="stretch" spacing={4}>
          <SkeletonCircle size="96px" />
          <SkeletonText noOfLines={4} spacing={2} />
        </VStack>
      </Box>
    );
  }

  if (error) {
    return wrapNetworkPage(<Text color="red.300">{error}</Text>);
  }

  if (soci.length === 0) {
    return wrapNetworkPage(
      <Box {...NETWORK_EMPTY_STATE_PROPS} p={8} textAlign="center">
        <Text fontSize="lg" mb={2}>
          Nessun socio tesserato
        </Text>
        <Text color="gray.400" fontSize="sm">
          Per usare il Network devi prima avere almeno un socio tesserato
          nell&apos;area account.
        </Text>
      </Box>
    );
  }

  if (!currentDraft) {
    return wrapNetworkPage(
      <>
        <SocioSelector
          soci={soci}
          selectedSocioId={selectedSocioId}
          onSelect={setSelectedSocioId}
        />
        <Box {...NETWORK_EMPTY_STATE_PROPS} p={8} textAlign="center">
          <Text color="gray.300">Seleziona un socio per iniziare</Text>
        </Box>
      </>
    );
  }

  const socioLabel = selectedSocio
    ? `${selectedSocio.nomeSocio || ''} ${selectedSocio.cognomeSocio || ''}`.trim()
    : '';

  return wrapNetworkPage(
    <>
      <SocioSelector
        soci={soci}
        selectedSocioId={selectedSocioId}
        onSelect={setSelectedSocioId}
      />

      {soci.length === 1 && socioLabel && (
        <Text fontSize="sm" color="gray.400" mt={-4} mb={6}>
          Profilo per <strong>{socioLabel}</strong>
        </Text>
      )}

      <NetworkProfilePreview
        draft={currentDraft}
        profile={currentProfile}
        allTags={allTags}
      />

      <ProfileVisibilityBanner
        isPublic={currentDraft.isPublic}
        onChange={(v) => handleDraftChange('isPublic', v)}
      />

      <VStack align="stretch" spacing={4} mb={{ base: 4, md: 8 }}>
        <NetworkSectionCard
          step={1}
          title="Identità"
          description="Foto, nome e biografia — il cuore del tuo profilo"
          isComplete={isIdentityComplete(currentDraft)}
          defaultOpen
        >
          <ProfileHeaderSection
            currentDraft={currentDraft}
            currentProfile={currentProfile}
            onDraftChange={handleDraftChange}
            onImageChange={handleImageChange}
            onRemoveImage={handleRemoveImage}
            uploadingImage={uploadingImage}
          />
        </NetworkSectionCard>

        <NetworkSectionCard
          step={2}
          title="Contatti e social"
          description="Come possono contattarti — tutto facoltativo"
          isComplete={isContactsComplete(currentDraft)}
          defaultOpen={false}
        >
          <SocialSection
            currentDraft={currentDraft}
            onDraftChange={handleDraftChange}
          />
        </NetworkSectionCard>

        <NetworkSectionCard
          step={3}
          title="Tag"
          description="Professioni, hobby e competenze per farti trovare"
          isComplete={isTagsComplete(currentDraft)}
          defaultOpen={false}
        >
          <TagsSection
            allTags={allTags}
            tagsLoading={tagsLoading}
            currentDraft={currentDraft}
            newTagNames={newTagNames}
            onToggleTag={toggleTagSelection}
            onNewTagNameChange={handleNewTagNameChange}
            onAddTag={handleAddTag}
          />
        </NetworkSectionCard>
      </VStack>

      {/* Desktop save */}
      <Box
        display={{ base: 'none', md: 'flex' }}
        alignItems="center"
        gap={3}
        p={4}
        bg="#36393f"
        borderRadius="12px"
        borderWidth="1px"
        borderColor="gray.600"
      >
        {hasUnsavedChanges && (
          <Text fontSize="sm" color="yellow.400" flex="1">
            Hai modifiche non salvate
          </Text>
        )}
        {!hasUnsavedChanges && (
          <Text fontSize="sm" color="gray.500" flex="1">
            Tutto salvato
          </Text>
        )}
        {saveButton}
      </Box>

      {/* Mobile sticky save bar */}
      <Box
        display={{ base: 'block', md: 'none' }}
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex="sticky"
        bg="gray.900"
        borderTopWidth="1px"
        borderColor="gray.600"
        px={4}
        py={3}
        pb="calc(12px + env(safe-area-inset-bottom, 0px))"
        boxShadow="0 -4px 20px rgba(0,0,0,0.4)"
      >
        {hasUnsavedChanges && (
          <Text fontSize="xs" color="yellow.400" mb={2} textAlign="center">
            Modifiche non salvate
          </Text>
        )}
        {saveButton}
      </Box>
    </>,
    { base: hasUnsavedChanges ? 28 : 6, md: 0 }
  );
};

export default NetworkProfiles;
